import { LOAN_PROGRAM_CODES } from '../../../config/organizations';
import { getLoanProgramTitle, isLoanProgramAvailable } from '../../../utils/loanProgramUtil';

export const LoanType = {
    AMBASSADOR: '6',
    ACCELERATOR: '7',
    ALLY: '8',
    ACTIVATOR: '9',
    ITIN_ACTIVATOR_WHOLESALE: '12',
    ITIN_ACCELERATOR_WHOLESALE: '13',
    SUPER_JUMBO: '14',
    ITIN_ACTIVATOR_CORRND: '21',
    ITIN_ACCELERATOR_CORRND: '22',
};

export const isAmbassador = (t) => t === LoanType.AMBASSADOR;
export const isAccelerator = (t) => t === LoanType.ACCELERATOR;
export const isAlly = (t) => t === LoanType.ALLY;
export const isActivator = (t) => t === LoanType.ACTIVATOR;
export const isSuperJumbo = (t) => t === LoanType.SUPER_JUMBO;
export const isItinActivator = (t) =>
    t === LoanType.ITIN_ACTIVATOR_WHOLESALE || t === LoanType.ITIN_ACTIVATOR_CORRND;
export const isItinAccelerator = (t) =>
    t === LoanType.ITIN_ACCELERATOR_WHOLESALE || t === LoanType.ITIN_ACCELERATOR_CORRND;

/** "Business purpose" products — they price off DSCR rather than DTI. */
export const isBusinessPurpose = (t) => isAmbassador(t) || isAccelerator(t) || isItinAccelerator(t);

/**
 * Which canonical product each pricing loan type represents.
 *
 * Quick Price keeps its own numeric ids because Polly's rate sheets are keyed
 * by them; this map is what ties those ids back to the products defined in
 * src/config/organizations.js, so the module can ask whether an org actually
 * carries a product without knowing any brand name.
 */
export const PROGRAM_BY_LOAN_TYPE = {
    [LoanType.AMBASSADOR]: LOAN_PROGRAM_CODES.PROGRAM_007,
    [LoanType.ACCELERATOR]: LOAN_PROGRAM_CODES.PROGRAM_002,
    [LoanType.ALLY]: LOAN_PROGRAM_CODES.PROGRAM_001,
    [LoanType.ACTIVATOR]: LOAN_PROGRAM_CODES.PROGRAM_004,
    [LoanType.ITIN_ACTIVATOR_WHOLESALE]: LOAN_PROGRAM_CODES.PROGRAM_006,
    [LoanType.ITIN_ACTIVATOR_CORRND]: LOAN_PROGRAM_CODES.PROGRAM_006,
    [LoanType.ITIN_ACCELERATOR_WHOLESALE]: LOAN_PROGRAM_CODES.PROGRAM_005,
    [LoanType.ITIN_ACCELERATOR_CORRND]: LOAN_PROGRAM_CODES.PROGRAM_005,
    [LoanType.SUPER_JUMBO]: LOAN_PROGRAM_CODES.PROGRAM_008,
};

/**
 * Results-panel headings, as a product name plus a fixed descriptor.
 *
 * `nameFrom` is the product whose org name fills `{name}` - which is not
 * always the same product the loan type prices as. The ITIN rows read "ITIN
 * Activator", built from the base Activator name, while they price as
 * PROGRAM_006 / PROGRAM_005 (whose Encompass names are ordered the other way
 * round, "ACTIVATOR ITIN"). Splitting the two keeps the heading's wording
 * intact while still following the brand.
 *
 * ChampsTPO renders exactly the strings this table replaced; SkyTPO gets
 * "Sunny (Full & Alt-Doc)", "ITIN Breezy (DSCR>=.80)" and so on.
 */
const PRODUCT_NAME_TEMPLATES = {
    [LoanType.ALLY]: {
        nameFrom: LOAN_PROGRAM_CODES.PROGRAM_001,
        template: '{name} (No Income/No Employment/No DTI)',
    },
    [LoanType.ACTIVATOR]: {
        nameFrom: LOAN_PROGRAM_CODES.PROGRAM_004,
        template: '{name} (Full & Alt-Doc)',
    },
    [LoanType.ITIN_ACTIVATOR_WHOLESALE]: {
        nameFrom: LOAN_PROGRAM_CODES.PROGRAM_004,
        template: 'ITIN {name} (ITIN Alt-Doc)',
    },
    [LoanType.ITIN_ACTIVATOR_CORRND]: {
        nameFrom: LOAN_PROGRAM_CODES.PROGRAM_004,
        template: 'ITIN {name} (ITIN Alt-Doc)',
    },
    [LoanType.ACCELERATOR]: {
        nameFrom: LOAN_PROGRAM_CODES.PROGRAM_002,
        template: '{name} Business Purpose',
    },
    [LoanType.ITIN_ACCELERATOR_WHOLESALE]: {
        nameFrom: LOAN_PROGRAM_CODES.PROGRAM_002,
        template: 'ITIN {name} (DSCR>=.80)',
    },
    [LoanType.ITIN_ACCELERATOR_CORRND]: {
        nameFrom: LOAN_PROGRAM_CODES.PROGRAM_002,
        template: 'ITIN {name} (DSCR>=.80)',
    },
    [LoanType.SUPER_JUMBO]: {
        nameFrom: LOAN_PROGRAM_CODES.PROGRAM_008,
        template: '{name} Full/Alt Doc',
    },
};

const fillTemplate = (entry) => {
    if (!entry) return '';

    const name = getLoanProgramTitle(entry.nameFrom);
    // No name means this org does not carry the product, and a heading reading
    // " (Full & Alt-Doc)" would be worse than none.
    if (!name) return '';

    return entry.template.replace('{name}', name);
};

/*
 * Polly product codes. Both companies price against the same Polly tenant, so
 * these are deliberately NOT per-org - the engine returns the same codes
 * whichever brand the broker is using, and interpretPollyResponse() matches
 * them with startsWith().
 */
const PRODUCT_NAMES_PREFIX = {
    [LoanType.AMBASSADOR]: 'BAMFN',
    [LoanType.ALLY]: 'CALLY',
    [LoanType.ACTIVATOR]: 'CACT',
    [LoanType.ITIN_ACTIVATOR_WHOLESALE]: 'CITIN',
    [LoanType.ITIN_ACTIVATOR_CORRND]: 'CITIN',
    [LoanType.ACCELERATOR]: 'BPACC',
    [LoanType.ITIN_ACCELERATOR_WHOLESALE]: 'BITIN',
    [LoanType.ITIN_ACCELERATOR_CORRND]: 'BITIN',
    [LoanType.SUPER_JUMBO]: 'NQMJUMBO',
};

/**
 * Ambassador splits into a full/alt-doc flavour and a DSCR flavour depending on
 * occupancy and income type (IsAmbassadorINC in the original).
 */
export function isAmbassadorIncome(typeCode, occupancy, incomeType) {
    return (
        isAmbassador(typeCode) && occupancy !== '0' && ['900', '9018', '9019'].includes(incomeType)
    );
}

/** Ambassador second-home full/alt-doc — the one case where I/O stays locked off. */
export function isAmbassadorSecondHomeIncome(typeCode, occupancy, incomeType) {
    return (
        isAmbassador(typeCode) && occupancy === '1' && ['900', '9018', '9019'].includes(incomeType)
    );
}

/** Accelerator priced on documented income instead of DSCR (IsAcceleratorINC). */
export function isAcceleratorIncome(typeCode, incomeType, incomeLabel) {
    return isAccelerator(typeCode) && incomeType !== '21' && !incomeLabel.includes('DSCR');
}

/*
 * Polly is a single shared tenant, so the program names it returns carry one
 * fixed vocabulary regardless of which company the broker is signed in as.
 * Match on that name, but show the org's own product name - PROGRAM_010 is
 * "Accelerator Equity First" for ChampsTPO and "Cloudy" for SkyTPO.
 */
const POLLY_EQUITY_FIRST_NAME = 'Equity First';
export const equityFirstLabel = (typeCode) => {
    if (isAlly(typeCode)) return LOAN_PROGRAM_CODES.PROGRAM_011;
    else if (isActivator(typeCode)) return LOAN_PROGRAM_CODES.PROGRAM_013;
    else if (isAccelerator(typeCode)) return LOAN_PROGRAM_CODES.PROGRAM_010;
    else return POLLY_EQUITY_FIRST_NAME;
}
/**
 * `pricedProgram` is the Polly program name when the pricing result holds a
 * single program; a single Equity First result is labelled by its own name.
 */
export function productName(typeCode, { occupancy, incomeType, pricedProgram, loanProductName } = {}) {
    if (pricedProgram && loanProductName) {
        if (pricedProgram?.includes(POLLY_EQUITY_FIRST_NAME)) {
            return fillTemplate({
                nameFrom: equityFirstLabel(typeCode),
                template: '{name}'
            });
        } else {
            return loanProductName;
        }
    } else {
        if (isAmbassador(typeCode)) {
            return fillTemplate({
                nameFrom: LOAN_PROGRAM_CODES.PROGRAM_007,
                template: isAmbassadorIncome(typeCode, occupancy, incomeType)
                    ? '{name} (Foreign National Full & Alt Doc)'
                    : '{name} (Foreign National DSCR/No-Ratio)',
            });
        }
        return fillTemplate(PRODUCT_NAME_TEMPLATES[typeCode]);
    }
}

export function productNamePrefix(typeCode, { occupancy, incomeType } = {}) {
    return PRODUCT_NAMES_PREFIX[typeCode] ?? '';
}

/** Loan amount above which Activator becomes Super Jumbo. */
export const SUPER_JUMBO_THRESHOLD = 3_000_000;

export function calculateLoanType({
    citizenship, occupancy, incomeType, incomeLabel, loanAmount, correspondent = false,
}) {
    if (citizenship === '3') return LoanType.AMBASSADOR;
    if (citizenship === '4') {
        if (incomeLabel.includes('DSCR')) {
            return correspondent
                ? LoanType.ITIN_ACCELERATOR_CORRND
                : LoanType.ITIN_ACCELERATOR_WHOLESALE;
        }
        return correspondent
            ? LoanType.ITIN_ACTIVATOR_CORRND
            : LoanType.ITIN_ACTIVATOR_WHOLESALE;
    }
    if (occupancy === '2') return LoanType.ACCELERATOR;
    if (incomeType !== '21') {
        return loanAmount > SUPER_JUMBO_THRESHOLD ? LoanType.SUPER_JUMBO : LoanType.ACTIVATOR;
    }
    /*
     * Ally is a wholesale-only product, and only in the companies that carry
     * it - SkyTPO has no equivalent. Activator is already the substitute this
     * function uses for correspondent, so an org without Ally takes the same
     * branch rather than pricing a product that does not exist for it.
     */
    const allyAvailable = isLoanProgramAvailable(PROGRAM_BY_LOAN_TYPE[LoanType.ALLY]);
    return correspondent || !allyAvailable ? LoanType.ACTIVATOR : LoanType.ALLY;
}
